import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ampli } from "../../ampli";
import { TrendChart } from "../TrendChart/TrendChart";
import { Loader } from "../Loader/Loader";
import { useCurrentUser } from "../../contexts/CurrentUser";
import Select from 'react-select';
import { DropdownMenu } from "../DropdownMenu/DropdownMenu";
import { Radiobutton } from "../DropdownMenu/Radiobutton";
import { ButtonsList } from "../DropdownMenu/ButtonsList";
import { buildImageUrl, downloadGoogleChartAsPNG, downloadGoogleChartAsSVG } from "../../utils/downloadChart";
import styles from '../../pages/NarrativePage/NarrativePage.module.scss';
import { palette } from "../../utils/colors";
import { ReactComponent as SettingsIcon } from '../../assets/settings.svg';
import { EvolutionTrendChart } from "../TrendChart/EvolutionTrendChart";


export const EvolutionChart = ({
  data,
  narrative,
  story=null,
  onChange,
  chartTab='publications',
  aggregation=null,
  breakdown=null,
  view=null,
  tabList=['publications', 'impressions', 'engagement'],
  filtersSettings=['aggregation', 'breakdown'],
  showModal=true,
  chartRef=null,
  isShare,
}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [totalEvolutionChart, setTotalEvolutionChart] = useState(chartTab);
  const [isTrendChartMenuOpen, setIsTrendChartMenuOpen] = useState(false);
  const trendChartRef = useRef(null);
  const trendChartCanvasRef = useRef(null);

  const defaultTabList = ['publications', 'impressions', 'engagement'];
  const sortedTabList = defaultTabList.filter(item => tabList.includes(item));

  useEffect(() => {
    setTotalEvolutionChart(sortedTabList[0])
  }, [])

  const statsAggregationOptions = [
    { value: 'DAY', label: t('Day') },
    { value: 'WEEK', label: t('Week') },
    { value: 'MONTH', label: t('Month') },
  ];
  const statsBreakdownOptions = [
    { value: null, label: t('All') },
    { value: 'PLATFORM', label: t('Platform') },
    { value: 'COUNTRY', label: t('Country') },
    { value: 'ACTOR_GROUP', label: t('Actor group') },
  ];

  const viewOptions = [
    {value: 'STANDARD', name: t('Standard')},
    {value: 'VALUES', name: t('Values')},
    {value: 'POINTS', name: t('Points')}
  ];

  const mapViewOptions = {
    'STANDARD': {value: 'STANDARD', name: t('Standard')},
    'VALUES': {value: 'VALUES', name: t('Values')},
    'POINTS': {value: 'POINTS', name: t('Points')}
  }

  const [statsAggregation, setStatsAggregation] = useState(
    aggregation || null,
  );

  const [statsBreakdown, setStatsBreakdown] = useState(
    breakdown || statsBreakdownOptions[0],
  );

  const [chartViewOptions, setChartViewOptions] = useState(view || viewOptions[0]);

  useEffect(() => {
    if (statsAggregation !== null) return;

    const aggValue = data?.objects?.[0]?.aggregation;
    if (aggValue) {
      const foundOption = statsAggregationOptions.find(opt => opt.value === aggValue);
      if (foundOption) {
        setStatsAggregation(foundOption);
      }
    }
  }, [data])

  const aggregationSelect = (
    <Select
      className="chart-select"
      onChange={(data) => {
        ampli.track({
          event_type: 'Change granularity in evolution chart',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            narrative_id: narrative.id,
            granularity: data.label,
          }
        });
        onChange(data, statsBreakdown);
        setStatsAggregation(data);
      }}
      value={statsAggregation}
      options={statsAggregationOptions}
    />
  );
  const breakdownSelect = (
    <Select
      className="chart-select"
      onChange={(data) => {
        ampli.track({
          event_type: 'Change breakdown in evolution chart',
          event_properties: {
            user_id: currentUser?.id,
            workspace_id: currentUser?.workspace_id,
            narrative_id: narrative.id,
            breakdown: data.label,
          }
        });
        onChange(statsAggregation, data);
        setStatsBreakdown(data);
      }}
      value={statsBreakdown}
      options={statsBreakdownOptions}
    />
  );

  let narrativeColor = null;
  if (data) {
    narrativeColor = Object.fromEntries(
      data?.objects?.map((narrative, i) => [narrative.id, palette[i % palette.length]])
    );
  }

  // Pages null `data` while refetching (breakdown / aggregation change). Keep
  // the last dataset on screen and let the chart show a loading overlay and
  // morph into the new data instead of unmounting to a spinner.
  const [displayData, setDisplayData] = useState(data);
  useEffect(() => {
    if (data) setDisplayData(data);
  }, [data]);

  const renderChart = () => {
    if (!displayData) return <Loader />;
    return (
      <EvolutionTrendChart
        rawdata={displayData}
        isLoading={!data}
        narrative={narrative}
        story={story}
        metric={totalEvolutionChart}
        showLegend={true}
        breakdown={statsBreakdown.value}
        narrativeColor={narrativeColor}
        view={chartViewOptions}
        showModal={showModal}
        chartRef={chartRef || trendChartRef}
        isShare={isShare}
      />
    );
  };

  return(
    <div className="report-section">
        <h3 className="trend-chart-controlls">
          {t('Evolution')}
          {sortedTabList?.includes('publications') ? <button
            type="button"
            className={totalEvolutionChart === 'publications' ? 'active' : ''}
            onClick={() => {
              ampli.track({
                event_type: 'Change metric in evolution chart',
                event_properties: {
                  user_id: currentUser?.id,
                  workspace_id: currentUser?.workspace_id,
                  narrative_id: narrative.id,
                  metric: 'publications',
                }
              });
              setTotalEvolutionChart('publications');
            }}
          >
            <span>{t('Publications')}</span>
          </button> : ''}
          {sortedTabList?.includes('impressions') ? <button
            type="button"
            className={totalEvolutionChart === 'impressions' ? 'active' : ''}
            onClick={() => {
              ampli.track({
                event_type: 'Change metric in evolution chart',
                event_properties: {
                  user_id: currentUser?.id,
                  workspace_id: currentUser?.workspace_id,
                  narrative_id: narrative.id,
                  metric: 'impressions',
                }
              });
              setTotalEvolutionChart('impressions');
            }}
          >
            <span>{t('Views')}</span>
          </button> : ''}
          {sortedTabList?.includes('engagement') ? <button
            type="button"
            className={totalEvolutionChart === 'engagement' ? 'active' : ''}
            onClick={() => {
              ampli.track({
                event_type: 'Change metric in evolution chart',
                event_properties: {
                  user_id: currentUser?.id,
                  workspace_id: currentUser?.workspace_id,
                  narrative_id: narrative.id,
                  metric: 'engagement',
                }
              });
              setTotalEvolutionChart('engagement');
            }}
          >
            <span>{t('Engagement')}</span>
          </button> : ''}
        </h3>

        <div className="card">
          <div className={"card-header " + styles.cardHeader}>
            <div className={'card-controlls ' + styles.cardControls}>
                {filtersSettings?.includes('breakdown') ? breakdownSelect : ''}
                {filtersSettings?.includes('aggregation') ?  aggregationSelect : ''}
                <DropdownMenu
                  icon={<SettingsIcon/>}
                  isOpen={isTrendChartMenuOpen}
                  onClick={() => setIsTrendChartMenuOpen(!isTrendChartMenuOpen)}
                  menuClassName='chartMenu'
                  header={t('Chart view')}
                >
                  <Radiobutton 
                    itemsList={viewOptions}
                    current={chartViewOptions.value}
                    onChange={(value) => setChartViewOptions(mapViewOptions[value])}
                  />

                  <ButtonsList
                    itemsList={[{value: 'PNG', name: t('Download as PNG')}, {value: 'SVG', name: t('Download as SVG')}]}
                    header={t('Download chart')}
                    onClick={(value) => {
                      let currentRef = chartRef || trendChartRef; 
                      let name = `${narrative.name}-evolution-chart-${statsBreakdown.label}-${statsAggregation.label}`
                      if(value === 'PNG') {
                        downloadGoogleChartAsPNG(name, currentRef);
                      } else if(value === 'SVG') {
                        downloadGoogleChartAsSVG(name, currentRef);
                      }
                    }}
                  />
                  
                </DropdownMenu>
            </div>
          </div>
          <div className="card-body" translate="no">
            {renderChart()}
          </div>
        </div>
      </div>
  )
}
